/**
 * Shared - Page Layout
 *
 * Common layout component for pages with header and content wrapper
 */

import type { ReactNode } from 'react';

import type { IconName } from '@repo/ui';

import { PageSkeleton } from './PageSkeleton';
import { DisabledState } from './DisabledState';
import { ErrorState } from './ErrorState';

export interface PageLayoutProps {
  /** Page title */
  title: string;
  /** Page description */
  description: string;
  /** Icon name for disabled state */
  icon?: IconName;
  /** Loading state */
  isLoading?: boolean;
  /** Disabled state */
  isDisabled?: boolean;
  /** Disabled state configuration */
  disabledState?: {
    title: string;
    description: string;
    helpText?: string;
  };
  /** Error state */
  isError?: boolean;
  /** Error state configuration */
  errorState?: {
    title: string;
    description: string;
    helpText?: string;
    onRetry?: () => void;
  };
  /** Page content */
  children: ReactNode;
  /** Custom header content */
  headerContent?: ReactNode;
  /** Custom skeleton props */
  skeletonProps?: React.ComponentProps<typeof PageSkeleton>;
  /** Custom skeleton component - if provided, will be used instead of default PageSkeleton */
  customSkeleton?: ReactNode;
}

export function PageLayout({
  title,
  description,
  icon,
  isLoading = false,
  isDisabled = false,
  disabledState,
  isError = false,
  errorState,
  children,
  headerContent,
  skeletonProps,
  customSkeleton,
}: PageLayoutProps) {
  // Show loading skeleton
  if (isLoading) {
    if (customSkeleton) {
      return <>{customSkeleton}</>;
    }
    return <PageSkeleton {...skeletonProps} />;
  }

  // Show error state (takes priority over disabled state)
  if (isError && errorState) {
    return (
      <ErrorState
        icon={icon || 'HiExclamation'}
        title={errorState.title}
        description={errorState.description}
        helpText={errorState.helpText}
        onRetry={errorState.onRetry}
      />
    );
  }

  // Show disabled state
  if (isDisabled && disabledState) {
    return (
      <DisabledState
        icon={icon || 'HiExclamation'}
        title={disabledState.title}
        description={disabledState.description}
        helpText={disabledState.helpText}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-xtnd-dark dark:text-xtnd-white">{title}</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{description}</p>
        </div>
        {headerContent && <div>{headerContent}</div>}
      </div>

      {/* Page Content */}
      {children}
    </div>
  );
}
